import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QrCode, Undo2, X, ChevronRight, MapPin, History, Camera, Zap, ZapOff, SwitchCamera, Aperture, Search, Plus } from "lucide-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { currentWarehouseUser } from "@/lib/warehouseSession";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currentUser = () =>
  currentWarehouseUser() ||
  (JSON.parse(localStorage.getItem("user") || "{}"))?.username ||
  null;

/**
 * Mobile Bin Assign
 * -----------------
 * Phone-first workflow for the store owner.
 *
 * Session flow:
 *   Setup screen  →  pick shelf (e.g. B0-0) + starting bin number (e.g. 1)
 *   Scan screen   →  auto-focused input; each scan / typed PLU:
 *                     · looks the product up
 *                     · silently overwrites its `location` to SHELF/0001
 *                     · previous location is pushed to `location_history`
 *                     · bin counter advances (0001 → 0002 → ...)
 *   Live list     →  shows every assignment in the current session; user
 *                    can tap × on any row to undo just that one, OR tap
 *                    "Undo Last" to reverse the most recent scan.
 *
 * Everything below is intentionally laid out for a smartphone: big inputs,
 * big buttons, thumb-reachable spacing, no horizontal scroll.
 */
export default function BinAssign() {
  const [screen, setScreen] = useState("setup"); // "setup" | "scan"
  const [shelf, setShelf] = useState("");
  const [nextBin, setNextBin] = useState(1);
  const [scanQuery, setScanQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState([]); // { plu, name, previous, new, bin, product_id }
  // Live-session state (PC scanner + phone display sync).
  const [sessionCode, setSessionCode] = useState("");
  const [joinCodeInput, setJoinCodeInput] = useState("");
  const sessionCodeRef = useRef("");
  const sessionPollRef = useRef(null);
  useEffect(() => { sessionCodeRef.current = sessionCode; }, [sessionCode]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [zoomRange, setZoomRange] = useState(null); // { min, max, step } if supported
  // Unknown-barcode helper (opens when a scan returns 404 so the user
  // can search the product on the phone, attach the barcode, and assign
  // without walking back to the office PC).
  const [unknownModal, setUnknownModal] = useState(null); // { code } | null
  const [unknownSearch, setUnknownSearch] = useState("");
  const [unknownResults, setUnknownResults] = useState([]);
  const [unknownBusy, setUnknownBusy] = useState(false);
  // Ref mirror so the session poll's closure can read the current modal
  // state without re-subscribing every render.
  const unknownModalRef = useRef(null);
  useEffect(() => { unknownModalRef.current = unknownModal; }, [unknownModal]);
  // Track barcodes THIS device pushed to the shared session so its own
  // poll doesn't re-open a local modal for a scan meant for the other
  // device (bug: PC scanned, pushed, then its own poll saw the pending
  // flag and opened the attach modal on the PC anyway).
  const pushedPendingRef = useRef(new Set());
  const detectorLoopRef = useRef(null);
  const scanRef = useRef(null);
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const cameraControlsRef = useRef(null);
  const lastCameraScanRef = useRef({ code: "", at: 0 });

  // Confirmation buffer: we only accept a scanned code once it appears
  // AT LEAST TWICE in a row within a short window. Prevents a single
  // misread frame from getting bound to the wrong product.
  const confirmBufferRef = useRef({ code: "", count: 0, firstAt: 0 });

  /**
   * Validate the mod-10 check digit on EAN-13 / UPC-A / EAN-8 / UPC-E.
   * Returns true for barcodes that pass (or non-numeric formats like Code
   * 128 / QR which don't have a checksum — those pass through).
   */
  const passesChecksum = (code) => {
    if (!/^\d+$/.test(code)) return true; // non-numeric formats have their own internal checks
    const len = code.length;
    if (![8, 12, 13].includes(len)) return true; // only validate the retail formats
    const digits = code.split("").map(Number);
    const check = digits.pop();
    // EAN-13 / UPC-A: reverse then weight 3,1,3,1... starting from rightmost data digit.
    // EAN-8: weight 3,1,3,1... starting from rightmost data digit.
    const reversed = digits.reverse();
    let sum = 0;
    for (let i = 0; i < reversed.length; i++) {
      sum += reversed[i] * (i % 2 === 0 ? 3 : 1);
    }
    const expected = (10 - (sum % 10)) % 10;
    return expected === check;
  };

  // Hot state that the camera loop needs to read on EVERY frame. React
  // closures capture state at render time, so the camera detector — created
  // once when the camera opens — would otherwise re-use the initial values
  // forever, producing bugs like "the same bin number assigned to two
  // different products in a row". Mirroring to refs keeps the callback in
  // sync with the latest values.
  const nextBinRef = useRef(1);
  const shelfRef = useRef("");
  const busyRef = useRef(false);
  const cooldownUntilRef = useRef(0);
  const afRefreshTimerRef = useRef(null);   // interval that pokes autofocus alive
  const [tapIndicator, setTapIndicator] = useState(null); // { x, y } screen % for the pulse dot
  const [cameraList, setCameraList] = useState([]);       // available rear cameras
  const [currentCameraId, setCurrentCameraId] = useState(null);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => { nextBinRef.current = nextBin; }, [nextBin]);
  useEffect(() => { shelfRef.current = shelf; }, [shelf]);
  useEffect(() => { busyRef.current = busy; }, [busy]);

  // Live search for the unknown-barcode modal. Debounced so we don't
  // hammer the API while the user is typing. Declared at the top level
  // (not conditionally) so the hook order stays stable across renders.
  useEffect(() => {
    if (!unknownModal) return;
    const q = unknownSearch.trim();
    if (q.length < 2) { setUnknownResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/bin-assign/search`, { params: { query: q, limit: 20 } });
        setUnknownResults(res.data.results || []);
      } catch { setUnknownResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [unknownSearch, unknownModal]);

  useEffect(() => {
    if (screen === "scan" && scanRef.current) {
      scanRef.current.focus();
    }
  }, [screen]);

  // Clean up the camera stream when leaving the page or closing the scanner.
  useEffect(() => {
    return () => stopCamera();
  }, []);

  const stopCamera = () => {
    try {
      cameraControlsRef.current?.stop?.();
    } catch { /* no-op */ }
    if (detectorLoopRef.current) {
      cancelAnimationFrame(detectorLoopRef.current);
      detectorLoopRef.current = null;
    }
    if (afRefreshTimerRef.current) {
      clearInterval(afRefreshTimerRef.current);
      afRefreshTimerRef.current = null;
    }
    cameraControlsRef.current = null;
    codeReaderRef.current = null;
    // Extra safety: force-release any tracks in case @zxing didn't clean up.
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks?.().forEach((t) => t.stop());
      videoRef.current.srcObject = null;
    }
    setTorchOn(false);
    setTorchSupported(false);
    setZoom(1);
    setZoomRange(null);
  };

  const toggleTorch = async () => {
    const stream = videoRef.current?.srcObject;
    if (!stream) return;
    const track = stream.getVideoTracks?.()[0];
    if (!track) return;
    try {
      const newState = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: newState }] });
      setTorchOn(newState);
    } catch {
      toast.error("Torch not supported on this device");
      setTorchSupported(false);
    }
  };

  // Tap the video to force a focus attempt at that point. Several strategies
  // are tried in sequence because focus API support varies wildly:
  //   • Chrome Android: `pointsOfInterest` + `focusMode: manual/single-shot`
  //   • Chrome (macOS/Linux): `focusMode: continuous` toggle
  //   • Safari: no direct focus API, but re-applying constraints often
  //     nudges the OS pipeline into re-running AF
  // We also show a visual pulse where the user tapped so it's obvious
  // something happened even when the phone's AF is silently working.
  const handleVideoTap = async (e) => {
    const stream = videoRef.current?.srcObject;
    const track = stream?.getVideoTracks?.()[0];
    if (!track) return;
    const rect = videoRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    // Show pulse dot
    setTapIndicator({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setTimeout(() => setTapIndicator(null), 700);

    const caps = track.getCapabilities?.() || {};
    const strategies = [];
    // Single-shot AF with point of interest — most native-app-like refocus.
    if (Array.isArray(caps.focusMode) && caps.focusMode.includes("single-shot")) {
      strategies.push({ advanced: [{ focusMode: "single-shot", pointsOfInterest: [{ x, y }] }] });
    }
    // Manual with POI (older API name still honoured by some builds).
    if (Array.isArray(caps.focusMode) && caps.focusMode.includes("manual")) {
      strategies.push({ advanced: [{ focusMode: "manual", pointsOfInterest: [{ x, y }] }] });
    }
    // Continuous refresh — safe fallback.
    if (Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
      strategies.push({ advanced: [{ focusMode: "continuous", pointsOfInterest: [{ x, y }] }] });
      strategies.push({ advanced: [{ focusMode: "continuous" }] });
    }
    // Distance sweep — some drivers only refocus when focusDistance is written.
    if (caps.focusDistance) {
      strategies.push({ advanced: [{ focusDistance: caps.focusDistance.min || 0.1 }] });
    }
    // At worst, apply an empty advanced list — sometimes wakes the pipeline.
    strategies.push({ advanced: [{}] });

    for (const s of strategies) {
      try {
        await track.applyConstraints(s);
        break;
      } catch { /* try next */ }
    }
  };

  const applyZoom = async (nextZoom) => {
    setZoom(nextZoom);
    const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ zoom: nextZoom }] });
    } catch { /* Safari: no zoom via constraints, ignore */ }
  };

  const processScannedCode = async (code) => {
    const now = Date.now();
    // 1. Global cooldown — no scan of ANY kind for a short window after
    //    the last successful assign so the camera can't fire a second
    //    product into the same bin before React has re-rendered.
    if (now < cooldownUntilRef.current) return;
    // 2. Skip in-flight scans (network request still running).
    if (busyRef.current) return;
    // 3. HARD CHECKSUM GATE — instantly reject codes that can't be real
    //    retail barcodes (mod-10 check fails). Eliminates 99% of misreads.
    if (!passesChecksum(code)) return;
    // 4. CONFIRMATION BUFFER — for NON-numeric formats (Code 128, QR)
    //    only. Numeric retail codes are already validated by the
    //    mathematical checksum above, so requiring a second identical
    //    read causes real barcodes to be missed when a stubborn one
    //    decodes slightly differently on intermediate frames. If the
    //    checksum passed AND the code is 8/12/13 digits, accept it now.
    const isRetailNumeric = /^\d+$/.test(code) && [8, 12, 13].includes(code.length);
    if (!isRetailNumeric) {
      const buf = confirmBufferRef.current;
      if (buf.code === code && now - buf.firstAt < 700) {
        buf.count += 1;
      } else {
        confirmBufferRef.current = { code, count: 1, firstAt: now };
        return; // wait for the second sighting
      }
      if (buf.count < 2) return;
    }
    // 5. Skip the SAME code within 2.5s (keeps the same barcode-in-frame
    //    from re-triggering after it's already been processed).
    if (code === lastCameraScanRef.current.code && now - lastCameraScanRef.current.at < 2500) return;
    lastCameraScanRef.current = { code, at: now };
    confirmBufferRef.current = { code: "", count: 0, firstAt: 0 };
    setScanQuery(code);
    if (navigator.vibrate) navigator.vibrate(30);
    await runAssign(code);
  };

  const openCamera = async (deviceId = null) => {
    setCameraOpen(true);
    // Give React a tick to mount the <video> element. 100ms is enough on
    // slow phones; 50ms sometimes wasn't for the initial cold mount.
    setTimeout(async () => {
      if (!videoRef.current) {
        toast.error("Camera element not ready. Please try again.");
        setCameraOpen(false);
        return;
      }
      try {
        // (enumerateDevices moved AFTER the first getUserMedia so we get
        // full labels/deviceIds — pre-permission the labels come back
        // empty on most browsers.)

        // Request the REAR camera at HD resolution with continuous autofocus.
        // If a specific deviceId is passed (user tapped "switch"), honour it.
        // Some browsers reject `advanced` constraints with a specific
        // deviceId, so we build tiers and try them in order.
        const baseVideo = deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: { ideal: "environment" } };
        const tiers = [
          { audio: false, video: { ...baseVideo, width: { ideal: 1920 }, height: { ideal: 1080 }, advanced: [{ focusMode: "continuous" }] } },
          { audio: false, video: { ...baseVideo, width: { ideal: 1920 }, height: { ideal: 1080 } } },
          { audio: false, video: baseVideo },
          { audio: false, video: true },
        ];
        let stream = null;
        let lastErr = null;
        for (const c of tiers) {
          try {
            stream = await navigator.mediaDevices.getUserMedia(c);
            break;
          } catch (e) {
            lastErr = e;
          }
        }
        if (!stream) throw lastErr || new Error("No camera stream available");

        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", "true");
        try { await videoRef.current.play(); } catch { /* Safari edge */ }
        // Remember which camera we're on so the "switch" button can cycle
        // to the next one.
        let currentSettings = {};
        try {
          currentSettings = stream.getVideoTracks()[0].getSettings?.() || {};
          setCurrentCameraId(currentSettings.deviceId || null);
        } catch { /* ignore */ }

        // AUTO-SWAP TO REAR: if we got the front camera despite asking
        // for `ideal: environment` (some WebViews ignore that hint), and
        // the caller didn't explicitly request a specific deviceId, try
        // once more with `exact: environment`. If that also fails we
        // silently keep the front cam rather than losing the whole flow.
        if (!deviceId && currentSettings.facingMode === "user") {
          try {
            const rearStream = await navigator.mediaDevices.getUserMedia({
              audio: false,
              video: {
                facingMode: { exact: "environment" },
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
            });
            // Success — stop the old (front) stream, swap in the rear one.
            stream.getTracks().forEach((t) => t.stop());
            stream = rearStream;
            videoRef.current.srcObject = stream;
            try { await videoRef.current.play(); } catch { /* ignore */ }
            try {
              currentSettings = stream.getVideoTracks()[0].getSettings?.() || {};
              setCurrentCameraId(currentSettings.deviceId || null);
            } catch { /* ignore */ }
          } catch {
            // No rear camera available or blocked — stay on front.
            toast.info("Rear camera unavailable — using front camera. Tap switch camera to try another.");
          }
        }

        // Re-enumerate NOW that permission is granted — before permission,
        // labels come back empty and deviceIds may be shuffled, so the
        // switch button couldn't correctly identify the rear cam.
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const cams = devices.filter((d) => d.kind === "videoinput");
          setCameraList(cams);
        } catch { /* ignore */ }

        // Detect torch + zoom capability so we can show/hide the buttons.
        const track = stream.getVideoTracks()[0];
        const caps = track.getCapabilities?.() || {};
        setTorchSupported(!!caps.torch);
        if (caps.zoom) {
          const zMin = caps.zoom.min || 1;
          const zMax = caps.zoom.max || 3;
          const zStep = caps.zoom.step || 0.1;
          setZoomRange({ min: zMin, max: zMax, step: zStep });
          // Auto-bump to 2x on open so small retail barcodes fill more of
          // the frame straight away. Users can slide back to 1x if they
          // need a wider field of view.
          const initialZoom = Math.min(2, zMax);
          if (initialZoom > zMin) {
            try { await track.applyConstraints({ advanced: [{ zoom: initialZoom }] }); } catch { /* ignore */ }
            setZoom(initialZoom);
          }
        }

        // Some phones (particularly older Android + iOS Safari) drop
        // continuous autofocus after the first lock. Poke it every 1.5s
        // to keep AF alive — silent no-op on drivers that ignore repeated
        // constraints.
        afRefreshTimerRef.current = setInterval(async () => {
          try {
            await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
          } catch { /* ignore */ }
        }, 1500);

        // Prefer native BarcodeDetector when available — it uses the OS
        // camera pipeline (with hardware autofocus) and is dramatically
        // more reliable on phones than the JS-based @zxing loop. Available
        // on Android Chrome and iOS 17+ Safari.
        if ("BarcodeDetector" in window) {
          try {
            const supported = await window.BarcodeDetector.getSupportedFormats();
            const wanted = [
              "ean_13", "ean_8", "upc_a", "upc_e",
              "code_128", "code_39", "code_93",
              "itf", "codabar", "qr_code",
            ].filter((f) => supported.includes(f));
            const detector = new window.BarcodeDetector({ formats: wanted });
            const scanLoop = async () => {
              if (!videoRef.current || !videoRef.current.srcObject) return;
              try {
                const codes = await detector.detect(videoRef.current);
                if (codes && codes.length > 0) {
                  processScannedCode(codes[0].rawValue);
                }
              } catch { /* ignore per-frame errors */ }
              detectorLoopRef.current = requestAnimationFrame(scanLoop);
            };
            detectorLoopRef.current = requestAnimationFrame(scanLoop);
            return; // Native path in use — skip @zxing fallback.
          } catch (e) {
            console.warn("Native BarcodeDetector failed, falling back to @zxing", e);
          }
        }

        // Fallback: @zxing/browser JS decoder.
        const hints = new Map();
        hints.set(DecodeHintType.TRY_HARDER, true);
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
          BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
          BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.CODE_93,
          BarcodeFormat.ITF, BarcodeFormat.CODABAR, BarcodeFormat.QR_CODE,
        ]);
        const reader = new BrowserMultiFormatReader(hints, {
          delayBetweenScanAttempts: 100,
          delayBetweenScanSuccess: 800,
        });
        codeReaderRef.current = reader;
        const controls = await reader.decodeFromStream(
          stream,
          videoRef.current,
          (result) => { if (result) processScannedCode(result.getText()); }
        );
        cameraControlsRef.current = controls;
      } catch (err) {
        console.error("Camera error:", err);
        const msg = err?.name === "NotAllowedError"
          ? "Camera permission denied. Enable it in your phone settings for this site."
          : err?.name === "NotFoundError"
          ? "No camera found on this device."
          : err?.name === "NotReadableError"
          ? "Camera is in use by another app. Close it and retry."
          : err?.name === "OverconstrainedError"
          ? "The camera can't match the requested settings on this phone."
          : (err?.message ? `Couldn't open camera: ${err.message}` : "Couldn't open camera.");
        toast.error(msg);
        setCameraOpen(false);
      }
    }, 100);
  };

  const switchCamera = async () => {
    if (cameraList.length < 2) {
      toast.info("Only one camera detected on this device");
      return;
    }
    // Find current index and pick the next one, wrapping.
    const idx = cameraList.findIndex((c) => c.deviceId === currentCameraId);
    const next = cameraList[(idx + 1) % cameraList.length];
    stopCamera();
    setCameraOpen(false);
    // Small delay so tracks fully release before re-requesting.
    setTimeout(() => openCamera(next.deviceId), 250);
    toast.info(next.label ? `Switched: ${next.label.substring(0, 30)}` : `Switched camera (${((idx + 1) % cameraList.length) + 1}/${cameraList.length})`);
  };

  /**
   * Take a still photo and decode it. On most phones the OS runs a full
   * autofocus + exposure cycle when taking a still, so this is far more
   * reliable than reading a soft preview frame — the workaround for
   * "camera won't focus" on macro-distance retail barcodes.
   */
  const captureAndDecode = async () => {
    if (capturing || !videoRef.current) return;
    setCapturing(true);
    try {
      const video = videoRef.current;
      const stream = video.srcObject;
      const track = stream?.getVideoTracks?.()[0];
      let bitmap = null;

      // Preferred path: ImageCapture API (Chrome Android). This asks the
      // OS for a full-quality still with real AF, exactly like the native
      // camera app would.
      if (track && "ImageCapture" in window) {
        try {
          const ic = new window.ImageCapture(track);
          // grabFrame is faster than takePhoto and doesn't play the
          // shutter sound — much better for a scan flow.
          bitmap = await ic.grabFrame();
        } catch {
          try {
            const blob = await new window.ImageCapture(track).takePhoto();
            bitmap = await createImageBitmap(blob);
          } catch { /* fall through */ }
        }
      }

      // Fallback: draw the current preview frame to a canvas.
      const canvas = document.createElement("canvas");
      const w = bitmap?.width || video.videoWidth || 1280;
      const h = bitmap?.height || video.videoHeight || 720;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (bitmap) {
        ctx.drawImage(bitmap, 0, 0, w, h);
      } else {
        ctx.drawImage(video, 0, 0, w, h);
      }

      // Try native BarcodeDetector on the still image first.
      let raw = null;
      if ("BarcodeDetector" in window) {
        try {
          const detector = new window.BarcodeDetector({
            formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "code_93", "itf", "codabar", "qr_code"],
          });
          const codes = await detector.detect(canvas);
          if (codes && codes.length > 0) raw = codes[0].rawValue;
        } catch { /* ignore */ }
      }

      // Fallback: @zxing decode from canvas.
      if (!raw) {
        try {
          const reader = new BrowserMultiFormatReader();
          const result = await reader.decodeFromCanvas(canvas);
          raw = result.getText();
        } catch { /* no result */ }
      }

      if (raw && passesChecksum(raw)) {
        // Successful still-capture decode: skip the confirm buffer (the user
        // deliberately captured this frame) and run straight through.
        lastCameraScanRef.current = { code: raw, at: Date.now() };
        setScanQuery(raw);
        if (navigator.vibrate) navigator.vibrate(35);
        await runAssign(raw);
      } else {
        if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
        toast.error("Couldn't read that. Hold steady, tap to focus, then try again.");
      }
    } finally {
      setCapturing(false);
    }
  };

  const closeCamera = () => {
    stopCamera();
    setCameraOpen(false);
    setTimeout(() => scanRef.current?.focus(), 50);
  };

  const startSession = async (opts = { withSharedSession: false }) => {
    const s = shelf.trim().toUpperCase();
    if (!s) {
      toast.error("Enter a shelf code (e.g. B0-0)");
      return;
    }
    if (nextBin < 1) {
      toast.error("Starting bin must be 1 or higher");
      return;
    }
    if (opts.withSharedSession) {
      try {
        const res = await axios.post(`${API}/bin-assign/session/start`, {
          shelf: s,
          starting_bin: nextBin,
          user: currentUser(),
        });
        setSessionCode(res.data.code);
        toast.success(`Shared session ${res.data.code} — enter this code on another device to see live updates`);
      } catch {
        toast.error("Couldn't start shared session");
        return;
      }
    }
    setShelf(s);
    setScreen("scan");
  };

  const joinSession = async () => {
    const code = joinCodeInput.trim().toUpperCase();
    if (!code) {
      toast.error("Enter the session code from the other device");
      return;
    }
    try {
      const res = await axios.get(`${API}/bin-assign/session/${code}`);
      setSessionCode(res.data.code);
      setShelf(res.data.shelf);
      setNextBin(res.data.next_bin_number);
      // Prime the local live list from the shared session so the joiner
      // sees existing rows immediately.
      const rows = (res.data.assignments || []).map((a) => ({
        product_id: a.product_id,
        plu: a.plu_code,
        name: a.name,
        previous: a.previous_location,
        new: a.new_location,
        bin: a.bin_number,
      }));
      setSession(rows);
      setScreen("scan");
      toast.success(`Joined session ${code}`);
    } catch (err) {
      toast.error(err?.response?.status === 404 ? "Session not found" : "Couldn't join session");
    }
  };

  const suggestNext = async () => {
    if (!shelf.trim()) return;
    try {
      const res = await axios.get(`${API}/bin-assign/next-bin`, {
        params: { shelf: shelf.trim().toUpperCase() },
      });
      setNextBin(res.data.next_bin_number || 1);
      toast.success(`Continuing from bin ${String(res.data.next_bin_number).padStart(4, "0")}`);
    } catch {
      toast.error("Couldn't fetch next bin");
    }
  };

  const runAssign = async (rawQuery) => {
    const q = (rawQuery ?? scanQuery).trim();
    if (!q) return;
    // Read from refs so consecutive camera scans always use the LATEST
    // bin/shelf/busy values — not the ones captured when the camera loop
    // was created. Setting busyRef synchronously also prevents the next
    // frame's scan from sneaking through the state-update lag.
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    const bin = nextBinRef.current;
    const currentShelf = shelfRef.current;
    try {
      // 1. Resolve barcode / PLU → product
      const lookup = await axios.get(`${API}/bin-assign/lookup`, { params: { query: q } });
      const product = lookup.data;

      // 2. Assign to current bin
      const assign = await axios.post(`${API}/bin-assign/assign`, {
        product_id: product.id,
        shelf: currentShelf,
        bin_number: bin,
        user: currentUser(),
        session_code: sessionCodeRef.current || null,
      });

      // 3. Update session state + advance the bin counter (both state AND
      //    ref, since the camera loop reads the ref directly).
      setSession((prev) => [
        {
          product_id: product.id,
          plu: product.plu_code,
          name: product.name,
          previous: assign.data.previous_location,
          new: assign.data.new_location,
          bin,
        },
        ...prev,
      ]);
      nextBinRef.current = assign.data.next_bin_number;
      setNextBin(assign.data.next_bin_number);
      setScanQuery("");

      // 4. Feedback + hard cooldown so a second product can't race into
      //    the same bin. 900ms is enough for a person to move on to the
      //    next item but tight enough that a real second scan isn't
      //    slowed down.
      cooldownUntilRef.current = Date.now() + 900;
      if (navigator.vibrate) navigator.vibrate(35);
      toast.success(`${product.name} → ${assign.data.new_location}`, { duration: 1500 });
    } catch (err) {
      if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
      // 404 = barcode not in the system.
      if (err?.response?.status === 404) {
        // Always clear the input straight away so the PC user (or the
        // Bluetooth scanner buffer) can keep scanning without walking
        // back to hit backspace on the office keyboard.
        setScanQuery("");
        if (sessionCodeRef.current) {
          // Shared session: push to the phone side so the person at the
          // aisle handles it. Don't open the modal on the PC.
          try {
            await axios.post(`${API}/bin-assign/session/${sessionCodeRef.current}/pending-unknown`, { barcode: q });
            // Remember we pushed this — otherwise the PC's own poll will
            // see the pending_unknown flag it just wrote and open the
            // modal locally anyway.
            pushedPendingRef.current.add(q);
            toast.info(`Barcode ${q} sent to the paired device to attach`, { duration: 3000 });
          } catch {
            // If pushing failed for some reason, fall back to the local modal.
            setUnknownModal({ code: q });
            setUnknownSearch("");
            setUnknownResults([]);
          }
        } else {
          // Solo mode: open the modal locally.
          setUnknownModal({ code: q });
          setUnknownSearch("");
          setUnknownResults([]);
        }
      } else {
        toast.error(err?.response?.data?.detail || "Scan failed");
        // Belt-and-braces: clear the input on ANY error so a stuck scan
        // never forces a walk back to the PC to hit backspace.
        setScanQuery("");
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
      // Re-focus so Bluetooth barcode scanners can send the next code
      // straight away (harmless when the on-screen camera is doing the scanning).
      setTimeout(() => scanRef.current?.focus(), 50);
    }
  };

  const handleScan = () => runAssign();

  const undoRow = async (row) => {
    try {
      await axios.post(`${API}/bin-assign/undo`, {
        product_id: row.product_id,
        bin_number: row.bin,
        session_code: sessionCodeRef.current || null,
      });
      setSession((prev) => prev.filter((r) => r.product_id !== row.product_id || r.bin !== row.bin));
      // Only decrement the counter if this WAS the most recent scan
      if (session[0]?.product_id === row.product_id && session[0]?.bin === row.bin) {
        setNextBin((n) => Math.max(1, n - 1));
      }
      toast.info(`Undone: ${row.name}`);
    } catch (err) {
      toast.error("Undo failed");
    }
  };

  const undoLast = () => {
    if (session.length === 0) return;
    undoRow(session[0]);
  };

  // Poll the shared session every 2s so PC & phone stay in lock-step.
  // Local state is authoritative for the CURRENT device's list order,
  // but the server's assignments list drives adds/removes from the other
  // side. We merge by product_id + bin_number so a local optimistic
  // update doesn't get overwritten mid-flight.
  useEffect(() => {
    if (screen !== "scan" || !sessionCode) {
      if (sessionPollRef.current) {
        clearInterval(sessionPollRef.current);
        sessionPollRef.current = null;
      }
      return;
    }
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/bin-assign/session/${sessionCode}`);
        const remoteRows = (res.data.assignments || []).map((a) => ({
          product_id: a.product_id,
          plu: a.plu_code,
          name: a.name,
          previous: a.previous_location,
          new: a.new_location,
          bin: a.bin_number,
        }));
        setSession(remoteRows);
        // Advance the local bin counter if the server got further ahead
        // (i.e. the OTHER device scanned something).
        if (typeof res.data.next_bin_number === "number" && res.data.next_bin_number > nextBinRef.current) {
          nextBinRef.current = res.data.next_bin_number;
          setNextBin(res.data.next_bin_number);
        }
        // Someone (usually the PC) just scanned an unknown barcode and
        // pushed it to the session for THIS device to resolve. Only open
        // the modal if we don't already have one open for the same code
        // (avoids flicker on every poll) AND we're not the device that
        // pushed the flag in the first place (otherwise the PC re-opens
        // the modal on itself right after routing the barcode to the phone).
        const pending = res.data.pending_unknown;
        const currentModal = unknownModalRef.current;
        const iPushedThis = pending?.barcode && pushedPendingRef.current.has(pending.barcode);
        if (pending?.barcode && !iPushedThis && (!currentModal || currentModal.code !== pending.barcode)) {
          setUnknownModal({ code: pending.barcode, fromSession: true });
          setUnknownSearch("");
          setUnknownResults([]);
          if (navigator.vibrate) navigator.vibrate([100, 60, 100]);
        }
        // Clean up the pushed-set once the flag has been cleared server-side
        // so a repeat scan of the same barcode later will re-route correctly.
        if (!pending?.barcode && pushedPendingRef.current.size > 0) {
          pushedPendingRef.current.clear();
        }
      } catch { /* ignore transient errors */ }
    };
    poll(); // fetch immediately
    sessionPollRef.current = setInterval(poll, 2000);
    return () => {
      if (sessionPollRef.current) {
        clearInterval(sessionPollRef.current);
        sessionPollRef.current = null;
      }
    };
  }, [screen, sessionCode]);

  const endSession = () => {
    if (session.length > 0 && !window.confirm(`End session? ${session.length} items were mapped. They stay on their bins — this just closes the session view.`)) {
      return;
    }
    setSession([]);
    setScanQuery("");
    setNextBin(1);
    setSessionCode("");
    setScreen("setup");
  };

  // ─────────────────────────── SETUP SCREEN ────────────────────────────
  if (screen === "setup") {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-6 flex flex-col" data-testid="bin-assign-setup">
        <div className="flex items-center gap-2 mb-6">
          <MapPin className="h-6 w-6 text-indigo-600" />
          <h1 className="text-xl font-bold text-slate-800">Bin Assign</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-5 flex-1">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Shelf code</label>
            <input
              type="text"
              value={shelf}
              onChange={(e) => setShelf(e.target.value.toUpperCase())}
              placeholder="e.g. B0-0"
              autoCapitalize="characters"
              className="w-full text-2xl font-mono tracking-wider rounded-xl border-2 border-slate-200 px-4 py-4 focus:border-indigo-500 focus:outline-none uppercase"
              data-testid="bin-assign-shelf-input"
            />
            <p className="text-xs text-slate-500 mt-2">Format: shelf/bin (e.g. B0-0/0001). Only enter the shelf here.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Starting bin number</label>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                value={nextBin}
                onChange={(e) => setNextBin(parseInt(e.target.value) || 1)}
                className="flex-1 text-2xl font-mono rounded-xl border-2 border-slate-200 px-4 py-4 focus:border-indigo-500 focus:outline-none"
                data-testid="bin-assign-start-bin-input"
              />
              <button
                onClick={suggestNext}
                type="button"
                className="px-4 rounded-xl border-2 border-slate-200 text-slate-600 text-sm active:bg-slate-100"
                data-testid="bin-assign-suggest-next-btn"
              >
                Continue<br />existing
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">Bins get formatted 4-digit (0001, 0002…) automatically.</p>
          </div>

          <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4">
            <div className="text-xs text-indigo-700 mb-1">Preview</div>
            <div className="text-2xl font-mono font-bold text-indigo-900">
              {(shelf || "SHELF").toUpperCase()}/{String(nextBin || 1).padStart(4, "0")}
            </div>
          </div>

          {/* Live session pairing (PC scanner + phone display) */}
          <div className="rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 p-4 space-y-3">
            <div>
              <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Live share (optional)</div>
              <p className="text-xs text-emerald-800 mt-1">
                Start a shared session on the PC, then enter the code on your phone to watch scans live and undo from either device.
              </p>
            </div>
            <button
              onClick={() => startSession({ withSharedSession: true })}
              disabled={!shelf.trim()}
              className="w-full bg-emerald-600 text-white font-semibold py-3 rounded-xl active:bg-emerald-700 disabled:bg-slate-300"
              data-testid="bin-assign-start-shared-btn"
            >
              Start shared session
            </button>
            <div className="flex items-center gap-2 text-xs text-emerald-700">
              <div className="flex-1 border-t border-emerald-200"></div>
              <span>or join one</span>
              <div className="flex-1 border-t border-emerald-200"></div>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={joinCodeInput}
                onChange={(e) => setJoinCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                placeholder="ABC123"
                maxLength={6}
                autoCapitalize="characters"
                className="flex-1 text-lg font-mono tracking-widest rounded-xl border-2 border-emerald-200 px-4 py-3 focus:border-emerald-500 focus:outline-none uppercase text-center bg-white"
                data-testid="bin-assign-join-code-input"
              />
              <button
                onClick={joinSession}
                disabled={joinCodeInput.trim().length < 4}
                className="px-5 rounded-xl bg-emerald-600 text-white font-semibold active:bg-emerald-700 disabled:bg-slate-300"
                data-testid="bin-assign-join-btn"
              >
                Join
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={() => startSession()}
          disabled={!shelf.trim()}
          className="mt-6 w-full bg-indigo-600 text-white text-lg font-semibold py-4 rounded-2xl shadow-lg active:bg-indigo-700 disabled:bg-slate-300 disabled:shadow-none flex items-center justify-center gap-2"
          data-testid="bin-assign-start-btn"
        >
          Start scanning (this device only) <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    );
  }

  // ─────────────────────────── SCAN SCREEN ─────────────────────────────
  const nextLocation = `${shelf}/${String(nextBin).padStart(4, "0")}`;

  // ── Unknown barcode helpers ────────────────────────────────────────
  const runUnknownSearch = async () => {
    const q = unknownSearch.trim();
    if (q.length < 2) { setUnknownResults([]); return; }
    try {
      const res = await axios.get(`${API}/bin-assign/search`, { params: { query: q, limit: 20 } });
      setUnknownResults(res.data.results || []);
    } catch {
      setUnknownResults([]);
    }
  };

  const attachAndAssign = async (product) => {
    if (!unknownModal || unknownBusy) return;
    setUnknownBusy(true);
    try {
      await axios.post(`${API}/bin-assign/attach-barcode`, {
        product_id: product.id,
        barcode: unknownModal.code,
      });
      toast.success(`Barcode ${unknownModal.code} attached to ${product.name}`);
      const wasFromSession = unknownModal.fromSession;
      setUnknownModal(null);
      setUnknownSearch("");
      setUnknownResults([]);
      // Clear the pending flag on the shared session so it doesn't
      // re-open on the next poll.
      if (wasFromSession && sessionCodeRef.current) {
        try { await axios.delete(`${API}/bin-assign/session/${sessionCodeRef.current}/pending-unknown`); } catch { /* ignore */ }
      }
      // Now that the barcode is attached, treat the original scan as
      // recognised — assign to the next bin the same way any normal
      // scan would.
      await runAssign(unknownModal.code);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't attach barcode");
    } finally {
      setUnknownBusy(false);
    }
  };

  const dismissUnknown = async () => {
    const wasFromSession = unknownModal?.fromSession;
    setUnknownModal(null);
    setUnknownSearch("");
    setUnknownResults([]);
    setScanQuery("");
    if (wasFromSession && sessionCodeRef.current) {
      try { await axios.delete(`${API}/bin-assign/session/${sessionCodeRef.current}/pending-unknown`); } catch { /* ignore */ }
    }
    setTimeout(() => scanRef.current?.focus(), 50);
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-4 flex flex-col" data-testid="bin-assign-scan">
      {/* Unknown-barcode modal: opens when a scan returns 404 so the user
          can search the product and attach the barcode on the spot. */}
      {unknownModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-end sm:items-center justify-center" data-testid="bin-assign-unknown-modal">
          <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Barcode not found</div>
                <div className="font-mono text-lg font-bold text-slate-800 mt-0.5">{unknownModal.code}</div>
              </div>
              <button
                onClick={dismissUnknown}
                className="p-2 rounded-full active:bg-slate-100"
                aria-label="Dismiss"
                data-testid="bin-assign-unknown-dismiss"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 border-b border-slate-100">
              <label className="block text-xs text-slate-500 mb-1">Search product by name or PLU</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  autoFocus
                  type="text"
                  value={unknownSearch}
                  onChange={(e) => setUnknownSearch(e.target.value)}
                  placeholder="e.g. MOL LIQUID or 3249"
                  className="w-full pl-9 pr-3 py-3 rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:outline-none"
                  data-testid="bin-assign-unknown-search-input"
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Tap a result to attach barcode <span className="font-mono">{unknownModal.code}</span> and assign to the next bin.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {unknownResults.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">
                  {unknownSearch.trim().length < 2 ? "Type at least 2 characters" : "No matches"}
                </div>
              ) : (
                unknownResults.map((p, idx) => (
                  <button
                    key={p.id}
                    onClick={() => attachAndAssign(p)}
                    disabled={unknownBusy}
                    className="w-full text-left px-4 py-3 border-b border-slate-50 active:bg-indigo-50 disabled:opacity-60 flex items-center gap-3"
                    data-testid={`bin-assign-unknown-result-${idx}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{p.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>PLU {p.plu_code || "—"}</span>
                        {p.barcode && <span className="font-mono">· BC {p.barcode}</span>}
                        {p.location && <span className="text-indigo-600 font-mono">· {p.location}</span>}
                      </div>
                    </div>
                    <Plus className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full-screen camera overlay */}
      {cameraOpen && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="bin-assign-camera-overlay">
          <div className="absolute top-0 left-0 right-0 z-10 px-4 py-3 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
            <div className="text-white">
              <div className="text-xs opacity-70 uppercase tracking-wide">Point at the barcode</div>
              <div className="font-mono text-lg font-bold">{nextLocation}</div>
            </div>
            <div className="flex items-center gap-2">
              {cameraList.length > 1 && (
                <button
                  onClick={switchCamera}
                  className="p-3 rounded-full bg-white/20 backdrop-blur text-white active:bg-white/30"
                  data-testid="bin-assign-switch-camera-btn"
                  aria-label="Switch camera"
                >
                  <SwitchCamera className="h-6 w-6" />
                </button>
              )}
              {torchSupported && (
                <button
                  onClick={toggleTorch}
                  className={`p-3 rounded-full backdrop-blur active:bg-white/30 ${torchOn ? "bg-amber-400 text-slate-900" : "bg-white/20 text-white"}`}
                  data-testid="bin-assign-torch-btn"
                  aria-label="Toggle flashlight"
                >
                  {torchOn ? <Zap className="h-6 w-6" /> : <ZapOff className="h-6 w-6" />}
                </button>
              )}
              <button
                onClick={closeCamera}
                className="p-3 rounded-full bg-white/20 backdrop-blur text-white active:bg-white/30"
                data-testid="bin-assign-close-camera-btn"
                aria-label="Close camera"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
            autoPlay
            onClick={handleVideoTap}
            data-testid="bin-assign-video"
          />
          {/* Tap-to-focus pulse */}
          {tapIndicator && (
            <div
              className="absolute w-16 h-16 border-2 border-yellow-300 rounded-full pointer-events-none animate-ping"
              style={{ left: tapIndicator.x - 32, top: tapIndicator.y - 32 }}
            />
          )}
          {/* Aiming reticle — wide + short, sized for retail EAN/UPC barcodes */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative">
              <div className="w-80 h-32 border-2 border-emerald-400 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"></div>
              {/* Animated red laser line */}
              <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-red-500/80 shadow-[0_0_8px_rgba(239,68,68,0.9)] animate-pulse"></div>
            </div>
          </div>
          {/* Zoom slider */}
          {zoomRange && (
            <div className="absolute bottom-44 left-6 right-6 z-10 flex items-center gap-3 bg-black/50 backdrop-blur rounded-full px-4 py-2">
              <span className="text-white text-xs font-semibold w-8">1x</span>
              <input
                type="range"
                min={zoomRange.min}
                max={zoomRange.max}
                step={zoomRange.step}
                value={zoom}
                onChange={(e) => applyZoom(parseFloat(e.target.value))}
                className="flex-1 accent-emerald-400"
                data-testid="bin-assign-zoom-slider"
              />
              <span className="text-white text-xs font-semibold w-10 text-right">{zoom.toFixed(1)}x</span>
            </div>
          )}
          {/* Big capture button — for stubborn/small barcodes where the
              live preview won't focus. Takes a still photo (OS runs full
              AF cycle) and decodes at full resolution. */}
          <button
            onClick={captureAndDecode}
            disabled={capturing}
            className="absolute bottom-24 left-1/2 -translate-x-1/2 h-16 w-16 rounded-full bg-white border-4 border-emerald-400 shadow-lg active:scale-95 disabled:opacity-60 flex items-center justify-center"
            data-testid="bin-assign-capture-btn"
            aria-label="Capture and decode"
          >
            <Aperture className={`h-8 w-8 ${capturing ? "text-slate-400 animate-spin" : "text-emerald-600"}`} />
          </button>
          {/* Hint */}
          <div className="absolute bottom-6 left-0 right-0 text-center text-white/90 text-sm px-6">
            Tap to focus. If it stays blurry, hit the round shutter button for a still-photo scan.
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Assigning to</div>
          <div className="text-2xl font-mono font-bold text-indigo-900" data-testid="bin-assign-current-location">{nextLocation}</div>
          {sessionCode && (
            <div className="mt-1 inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-mono" data-testid="bin-assign-session-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Live · {sessionCode}
            </div>
          )}
        </div>
        <button
          onClick={endSession}
          className="text-sm text-slate-500 border border-slate-300 px-3 py-1.5 rounded-lg active:bg-slate-100"
          data-testid="bin-assign-end-session-btn"
        >
          End
        </button>
      </div>

      {/* Scan input */}
      <div className="bg-white rounded-2xl shadow-sm p-4 mb-3">
        <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">Scan barcode or type PLU</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              ref={scanRef}
              type="text"
              value={scanQuery}
              onChange={(e) => setScanQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleScan()}
              placeholder="Barcode / PLU"
              inputMode="text"
              autoComplete="off"
              className="w-full pl-11 pr-4 py-4 text-lg font-mono rounded-xl border-2 border-slate-200 focus:border-indigo-500 focus:outline-none"
              data-testid="bin-assign-scan-input"
            />
          </div>
          <button
            onClick={openCamera}
            className="px-4 rounded-xl bg-emerald-600 text-white font-semibold active:bg-emerald-700 flex items-center justify-center"
            data-testid="bin-assign-open-camera-btn"
            aria-label="Scan with camera"
          >
            <Camera className="h-5 w-5" />
          </button>
          <button
            onClick={handleScan}
            disabled={!scanQuery.trim() || busy}
            className="px-5 rounded-xl bg-indigo-600 text-white font-semibold active:bg-indigo-700 disabled:bg-slate-300"
            data-testid="bin-assign-assign-btn"
          >
            {busy ? "…" : "Add"}
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">Tap the green camera to scan with your phone. Or use a Bluetooth scanner — it types into the box above.</p>
      </div>

      {/* Undo Last */}
      {session.length > 0 && (
        <button
          onClick={undoLast}
          className="mb-3 flex items-center justify-center gap-2 text-sm text-amber-700 border border-amber-300 bg-amber-50 py-3 rounded-xl active:bg-amber-100"
          data-testid="bin-assign-undo-last-btn"
        >
          <Undo2 className="h-4 w-4" /> Undo last scan ({session[0].name.substring(0, 24)})
        </button>
      )}

      {/* Live list */}
      <div className="bg-white rounded-2xl shadow-sm flex-1 overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">
            This session <span className="text-slate-400 font-normal">({session.length})</span>
          </div>
          {session.length > 0 && (
            <div className="text-xs text-slate-400">Tap × to remove any row</div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {session.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">
              Scan or type a barcode to assign it to {nextLocation}
            </div>
          ) : (
            session.map((row, idx) => (
              <div
                key={`${row.product_id}-${row.bin}-${idx}`}
                className="flex items-center gap-3 px-4 py-3 border-b border-slate-50 active:bg-slate-50"
                data-testid={`bin-assign-session-row-${idx}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{row.name}</div>
                  <div className="flex items-center gap-2 text-xs mt-0.5">
                    <span className="font-mono text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                      {row.new}
                    </span>
                    {row.previous && (
                      <span className="text-slate-400 flex items-center gap-1">
                        <History className="h-3 w-3" />
                        was {row.previous}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">PLU {row.plu || "—"}</div>
                </div>
                <button
                  onClick={() => undoRow(row)}
                  className="p-2 text-slate-400 active:text-red-600 active:bg-red-50 rounded-full"
                  data-testid={`bin-assign-remove-row-${idx}`}
                  aria-label="Remove row"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
